// Fortune Sourcings, Country × Product compliance pages.
//
// Data-driven library covering the most common combinations of products
// imported from China. Each entry includes:
//   - HS chapter / typical HS codes
//   - Latest 2026 customs duty + VAT/sales-tax structure (verified at build time)
//   - Mandatory certifications & destination-country rules
//   - Major ports / clearance lead time
//   - Recommended Incoterm
//   - Standard Fortune QC / docs notes
//
// Numbers below are duty bands as published by destination customs as of
// February 2026, actual duty depends on exact HS code and any current free-trade
// agreement. Always verify by HS code at quote time. Keep this file as the
// single source of truth, admin can also edit individual cells via the CMS.

export const COUNTRIES = [
  { code: "ae", name: "United Arab Emirates", currency: "AED",
    ports: "Jebel Ali (Dubai) · Khalifa (Abu Dhabi) · Sharjah",
    tax: "5% VAT", duty_default: "5% on CIF (most goods)",
    notes: "GCC Common Customs Tariff. ESMA conformity required for many regulated categories. Free zones (JAFZA, KIZAD) offer 0% duty re-export." },
  { code: "us", name: "United States", currency: "USD",
    ports: "Los Angeles · Long Beach · NY/NJ · Savannah · Houston",
    tax: "Sales tax, state-by-state",
    duty_default: "Per HTSUS line, plus current Section 301 tariffs on most China-origin goods",
    notes: "Section 301 surcharges (List 1-4) apply to China-origin goods. Customs uses HTSUS classification. CBP entry must include MID and proper HTSUS." },
  { code: "in", name: "India", currency: "INR",
    ports: "Nhava Sheva · Mundra · Chennai · Kolkata · ICD Tughlakabad",
    tax: "18% IGST (most), 28% on luxury",
    duty_default: "BCD 0-30% + 10% Social Welfare Surcharge + IGST",
    notes: "BIS for electronics, FSSAI for food contact. FORM-E required from authorised dealer banking." },
  { code: "gb", name: "United Kingdom", currency: "GBP",
    ports: "Felixstowe · Southampton · London Gateway",
    tax: "20% VAT", duty_default: "Per UK Global Tariff",
    notes: "UKCA marking now mandatory for most regulated goods. CE marking is no longer accepted for new placements as of 2025-2026." },
  { code: "de", name: "Germany / EU", currency: "EUR",
    ports: "Hamburg · Bremerhaven · Rotterdam (NL) · Antwerp (BE)",
    tax: "19% VAT (DE), varies by member state",
    duty_default: "Per EU TARIC, most consumer goods 0-17%",
    notes: "CE marking, RoHS, REACH all apply. Importer of Record obligations under EU GPSR." },
  { code: "au", name: "Australia", currency: "AUD",
    ports: "Sydney · Melbourne · Brisbane · Fremantle",
    tax: "10% GST",
    duty_default: "Most goods 0-5% (ChAFTA preferential where COO claimed)",
    notes: "ChAFTA China-Australia FTA, 0% duty on many tariff lines with valid Form FTA-certificate." },
  { code: "sa", name: "Saudi Arabia", currency: "SAR",
    ports: "Jeddah · Dammam",
    tax: "15% VAT",
    duty_default: "5-15% (most consumer goods 5%)",
    notes: "SABER + SASO PCoC mandatory for most regulated categories. SFDA approval for medical / cosmetic." },
  { code: "ke", name: "Kenya", currency: "KES",
    ports: "Mombasa",
    tax: "16% VAT",
    duty_default: "EAC CET, 0% / 10% / 25% / 35% bands",
    notes: "PVoC (Pre-shipment Verification of Conformity) by Bureau Veritas / SGS / Intertek. KEBS mandatory standards." },
  { code: "ng", name: "Nigeria", currency: "NGN",
    ports: "Apapa · Tin Can Island · Onne",
    tax: "7.5% VAT",
    duty_default: "ECOWAS CET, 0% / 5% / 10% / 20% / 35% bands",
    notes: "SONCAP (Standards Organisation of Nigeria Conformity) required for most products. Form-M mandatory pre-import." },
  { code: "br", name: "Brazil", currency: "BRL",
    ports: "Santos · Itajaí · Paranaguá",
    tax: "ICMS state VAT (varies 17-19%) + IPI + PIS/COFINS",
    duty_default: "II, 0-35% per Mercosur CET",
    notes: "Importer must hold RADAR licence. INMETRO certifications for many categories." },
  { code: "ca", name: "Canada", currency: "CAD",
    ports: "Vancouver · Montreal · Halifax · Prince Rupert",
    tax: "5% GST + provincial",
    duty_default: "Per CCT, most consumer goods 0-18%",
    notes: "ISED (formerly IC) for radio devices. CSA / cUL for electrical." },
  { code: "za", name: "South Africa", currency: "ZAR",
    ports: "Durban · Cape Town · Port Elizabeth",
    tax: "15% VAT",
    duty_default: "Per SACU, 0-45%",
    notes: "ITAC permit required for many categories. NRCS LOA for electrical and toys." },
];

export const PRODUCTS = [
  { slug: "furniture",
    name: "Furniture",
    hs: "94 (esp. 9403 / 9401)",
    fortune_note: "Foshan / Shunde / Dongguan / Anji clusters. Multi-supplier consolidation, 40HQ-loaded mark-separated by SKU group." },
  { slug: "tiles-sanitaryware",
    name: "Tiles & Sanitaryware",
    hs: "6907 (tiles) · 6910 (sanitaryware) · 6802 (stone)",
    fortune_note: "Foshan ceramic cluster. SKU-by-SKU shade match, multi-shade consolidation, stretch-wrapped pallet loading." },
  { slug: "luggage",
    name: "Luggage & Travel Goods",
    hs: "4202",
    fortune_note: "Pinghu (hard-shell) / Quanzhou (premium leather). Branded packaging, FN AS labelling, 40HQ optimised." },
  { slug: "mobile-accessories",
    name: "Mobile Phone Accessories",
    hs: "8517 (chargers) · 8504 (adapters) · 4202 (cases)",
    fortune_note: "Shenzhen Huaqiangbei / Bao'an. CE / FCC / BIS / SASO testing as required by destination." },
  { slug: "machinery",
    name: "Industrial Machinery",
    hs: "84 (most lines)",
    fortune_note: "Wuxi / Jinan / Qingdao. FAT-witnessed, RCC + commissioning paperchain preserved." },
  { slug: "mining-machinery",
    name: "Mining Machinery",
    hs: "8474 / 8430 / 8457",
    fortune_note: "Yantai / Zhengzhou. CE Machinery Directive + ATEX where applicable. Heavy-lift container or RoRo coordination." },
  { slug: "bopet-films",
    name: "BOPET Films",
    hs: "3920 (esp. 392062)",
    fortune_note: "Anhui / Jiangsu. Metallised, thermal-laminating, packaging-grade. AQL on every roll for thickness, gauge, surface." },
  { slug: "textiles",
    name: "Textiles",
    hs: "50-63 (chapter range)",
    fortune_note: "Keqiao / Shaoxing. Apparel fabrics, knits, wovens, prints. Pantone-verified colour QC, formaldehyde testing." },
  { slug: "technical-fabrics",
    name: "Technical Fabrics",
    hs: "5407 / 5903 / 5911",
    fortune_note: "Wujiang / Tongxiang. Geotextile, industrial mesh, coated, flame-retardant. EN 13501 / ASTM testing as required." },
  { slug: "stationery",
    name: "Stationery",
    hs: "4820 / 9608 / 9609",
    fortune_note: "Ningbo / Wenzhou / Yiwu. Pens, paper, office, school. ASTM F963 toy-overlap testing where applicable." },
  { slug: "yiwu-commodities",
    name: "Yiwu Small Commodities",
    hs: "Multi-chapter consolidation",
    fortune_note: "Yiwu Markets 1-5, 32-vendor average per programme, single packing list per container." },
  { slug: "lighting",
    name: "Lighting",
    hs: "9405",
    fortune_note: "Zhongshan Guzhen. CE / UL / ENEC / SAA / RCM / SASO certifications managed by Fortune QC desk." },
];

// ---------------------------------------------------------------------------
// Country × Product duty matrix
// Notation: "%" = ad-valorem on CIF unless noted. Rates are consolidated as of
// February 2026, verify by exact HS code at quote time.
// ---------------------------------------------------------------------------
export const DUTY_MATRIX = {
  // Country code → Product slug → { duty, sales_tax, certs, notes }
  ae: {
    furniture:           { duty: "5%",  certs: ["ESMA, flammability for upholstered (where applicable)"], notes: "5% GCC tariff. JAFZA/KIZAD re-export 0%." },
    "tiles-sanitaryware":{ duty: "5%",  certs: ["ESMA, Construction Products"], notes: "Wet-sample retention is recommended for shade-match disputes." },
    luggage:             { duty: "5%",  certs: [],  notes: "5% GCC tariff. Leather goods may attract additional inspection on landing." },
    "mobile-accessories":{ duty: "5%",  certs: ["ESMA Telecom (TDRA)", "Type approval for radio"], notes: "Bluetooth / WiFi require TDRA registration." },
    machinery:           { duty: "5%",  certs: ["ESMA G-Mark (where applicable)"], notes: "" },
    "mining-machinery":  { duty: "5%",  certs: ["ESMA G-Mark", "ATEX docs (oil & gas)"], notes: "Heavy-lift coordination from Ningbo or Shanghai." },
    "bopet-films":       { duty: "5%",  certs: [], notes: "Industrial input, no consumer cert required." },
    textiles:            { duty: "5%",  certs: ["ESMA conformity for apparel"], notes: "" },
    "technical-fabrics": { duty: "5%",  certs: [], notes: "" },
    stationery:          { duty: "5%",  certs: [], notes: "" },
    "yiwu-commodities":  { duty: "5%",  certs: [], notes: "Multi-SKU declaration must be HS-coded line-by-line." },
    lighting:            { duty: "5%",  certs: ["ESMA Lighting"], notes: "MEPS (energy efficiency) registration required." },
  },
  us: {
    furniture:           { duty: "0-5% MFN + Section 301 (typically 25%)", certs: ["CARB Phase 2 formaldehyde", "CAL 117 fire (CA)", "CPSC bedding"], notes: "Most upholstered furniture 25% Section 301. Tariff engineering by HTSUS sub-line is critical." },
    "tiles-sanitaryware":{ duty: "0-8.5% MFN + Section 301 (25%)", certs: ["EPA WaterSense (faucets)", "CalGreen"], notes: "ANSI A137.1 for ceramic tile compliance." },
    luggage:             { duty: "MFN 6.3-20% + Section 301 (25%)", certs: ["CPSC tracking label (children)"], notes: "Soft-sided luggage 6.3% MFN; hard-shell 20% MFN; both add Section 301." },
    "mobile-accessories":{ duty: "0% MFN + Section 301 (25%)", certs: ["FCC", "UL (charging)", "Prop 65 (CA)"], notes: "Section 301 List 4A removed temporarily on some chargers, verify at quote." },
    machinery:           { duty: "0-4% MFN + Section 301 (25%)", certs: ["UL / ETL", "OSHA"], notes: "Section 301 List 1-3 hits most industrial machinery." },
    "mining-machinery":  { duty: "0% MFN + Section 301 (25%)", certs: ["MSHA where applicable"], notes: "Section 301 List 3." },
    "bopet-films":       { duty: "MFN 4.2% + Section 301 (25%) + AD/CVD on certain origins", certs: [], notes: "Anti-dumping risk, confirm product specification falls outside the AD scope at quote." },
    textiles:            { duty: "MFN 0-32% + Section 301 (varies)", certs: ["FTC labelling", "CPSC flammability"], notes: "Cotton + Wool require detailed fibre declaration." },
    "technical-fabrics": { duty: "MFN 0-17% + Section 301 (varies)", certs: [], notes: "" },
    stationery:          { duty: "MFN 0-4% + Section 301 (25%)", certs: ["ASTM F963 (toy-overlap)"], notes: "" },
    "yiwu-commodities":  { duty: "Multi-line, Section 301 by HTSUS", certs: ["CPSC where applicable"], notes: "Each SKU declared on its own HTSUS line." },
    lighting:            { duty: "MFN 3.9% + Section 301 (25%)", certs: ["UL", "Energy Star (registered models)", "FCC"], notes: "" },
  },
  in: {
    furniture:           { duty: "BCD 25% + 10% SWC + 18% IGST", certs: ["BIS for some treated wood"], notes: "FORM-E required from AD bank. Effective rate ~50% on CIF." },
    "tiles-sanitaryware":{ duty: "BCD 10-15% + 10% SWC + 18% IGST", certs: ["BIS IS 13753 (tiles)", "BIS for sanitaryware"], notes: "Anti-dumping on certain Chinese tiles, verify at quote." },
    luggage:             { duty: "BCD 30% + 10% SWC + 18% IGST", certs: [], notes: "Effective rate ~58-60%. Hard-shell vs soft-shell HS distinction important." },
    "mobile-accessories":{ duty: "BCD 15-20% + 10% SWC + 18% IGST", certs: ["BIS Compulsory Registration (CRO)", "WPC for radio"], notes: "BIS CRO mandatory for most chargers, power banks, batteries." },
    machinery:           { duty: "BCD 7.5% + 10% SWC + 18% IGST", certs: [], notes: "Concessional duty under EPCG / Project Imports for capital goods." },
    "mining-machinery":  { duty: "BCD 7.5% + 10% SWC + 18% IGST", certs: ["DGMS (where applicable)"], notes: "Concessional under Project Imports." },
    "bopet-films":       { duty: "BCD 10% + 10% SWC + 18% IGST + ADD on certain Chinese origins", certs: [], notes: "Confirm ADD scope at quote." },
    textiles:            { duty: "BCD 10-25% + 10% SWC + 18% IGST", certs: ["BIS for certain technical textiles"], notes: "" },
    "technical-fabrics": { duty: "BCD 10-25% + 10% SWC + 18% IGST", certs: [], notes: "" },
    stationery:          { duty: "BCD 10% + 10% SWC + 18% IGST", certs: [], notes: "" },
    "yiwu-commodities":  { duty: "Per-SKU classification; effective ~30-60%", certs: [], notes: "Multi-SKU consolidation must be line-itemised." },
    lighting:            { duty: "BCD 20% + 10% SWC + 18% IGST", certs: ["BIS IS 16102 (LED)"], notes: "BIS CRO mandatory for LED bulbs and fixtures." },
  },
  gb: {
    furniture:           { duty: "0% UKGT + 20% VAT", certs: ["UKCA, Furniture & Furnishings (Fire Safety)"], notes: "FFFSR fire-retardancy compliance required for upholstered." },
    "tiles-sanitaryware":{ duty: "0% UKGT + 20% VAT", certs: ["UKCA Construction Products"], notes: "WRAS approval for water-contact fittings." },
    luggage:             { duty: "0-6% UKGT + 20% VAT", certs: [], notes: "" },
    "mobile-accessories":{ duty: "0% UKGT + 20% VAT", certs: ["UKCA"], notes: "UKCA marking now required (replacing CE for GB market)." },
    machinery:           { duty: "0% UKGT + 20% VAT", certs: ["UKCA Machinery", "UKCA EMC"], notes: "" },
    "mining-machinery":  { duty: "0% UKGT + 20% VAT", certs: ["UKCA Machinery", "UKCA ATEX"], notes: "" },
    "bopet-films":       { duty: "0% UKGT + 20% VAT", certs: [], notes: "" },
    textiles:            { duty: "0-12% UKGT + 20% VAT", certs: ["UKCA where regulated"], notes: "" },
    "technical-fabrics": { duty: "0-8% UKGT + 20% VAT", certs: [], notes: "" },
    stationery:          { duty: "0% UKGT + 20% VAT", certs: [], notes: "" },
    "yiwu-commodities":  { duty: "Per HS, generally 0-6% + 20% VAT", certs: [], notes: "" },
    lighting:            { duty: "0-4.7% UKGT + 20% VAT", certs: ["UKCA", "WEEE", "RoHS"], notes: "" },
  },
  de: {
    furniture:           { duty: "0-2.7% TARIC + 19% VAT", certs: ["CE", "DIN EN 16139 (contract)"], notes: "EU GPSR Importer of Record obligations." },
    "tiles-sanitaryware":{ duty: "0-4.7% TARIC + 19% VAT", certs: ["CE Construction Products (CPR)"], notes: "DoP (Declaration of Performance) required." },
    luggage:             { duty: "2.7-9.7% TARIC + 19% VAT", certs: [], notes: "" },
    "mobile-accessories":{ duty: "0% TARIC + 19% VAT", certs: ["CE LVD/EMC", "RoHS", "REACH"], notes: "WEEE producer registration required." },
    machinery:           { duty: "0-4.7% TARIC + 19% VAT", certs: ["CE Machinery Directive", "EMC", "RoHS"], notes: "Risk assessment + technical file mandatory." },
    "mining-machinery":  { duty: "0% TARIC + 19% VAT", certs: ["CE Machinery", "ATEX (where applicable)"], notes: "" },
    "bopet-films":       { duty: "6.5% TARIC + 19% VAT, anti-dumping varies", certs: [], notes: "Anti-dumping in force on specified Chinese exporters, verify at quote." },
    textiles:            { duty: "0-12% TARIC + 19% VAT", certs: ["REACH Annex XVII", "OEKO-TEX (commercial)"], notes: "" },
    "technical-fabrics": { duty: "0-8% TARIC + 19% VAT", certs: [], notes: "" },
    stationery:          { duty: "0% TARIC + 19% VAT", certs: [], notes: "" },
    "yiwu-commodities":  { duty: "Per HS, generally 0-6.5% + 19% VAT", certs: [], notes: "" },
    lighting:            { duty: "2.7-4.7% TARIC + 19% VAT", certs: ["CE", "RoHS", "ErP"], notes: "" },
  },
  au: {
    furniture:           { duty: "0% (ChAFTA) + 10% GST", certs: ["AS/NZS 4220 contract"], notes: "Form FTA-COO, China origin claim." },
    "tiles-sanitaryware":{ duty: "0% (ChAFTA) + 10% GST", certs: ["WaterMark", "WELS (water-using fixtures)"], notes: "" },
    luggage:             { duty: "5% / 0% ChAFTA + 10% GST", certs: [], notes: "" },
    "mobile-accessories":{ duty: "0% (ChAFTA) + 10% GST", certs: ["RCM (ACMA)", "EESS registration"], notes: "" },
    machinery:           { duty: "0% (ChAFTA) + 10% GST", certs: [], notes: "" },
    "mining-machinery":  { duty: "0% (ChAFTA) + 10% GST", certs: ["EESS where applicable"], notes: "" },
    "bopet-films":       { duty: "0% (ChAFTA) + 10% GST", certs: [], notes: "" },
    textiles:            { duty: "0-5% / 0% ChAFTA + 10% GST", certs: ["AS/NZS labelling"], notes: "" },
    "technical-fabrics": { duty: "0% (ChAFTA) + 10% GST", certs: [], notes: "" },
    stationery:          { duty: "0% (ChAFTA) + 10% GST", certs: [], notes: "" },
    "yiwu-commodities":  { duty: "Most lines 0% under ChAFTA + 10% GST", certs: [], notes: "Multi-SKU classification still required." },
    lighting:            { duty: "0% (ChAFTA) + 10% GST", certs: ["EESS (electrical)", "GEMS (energy)"], notes: "" },
  },
  sa: {
    furniture:           { duty: "5% + 15% VAT", certs: ["SABER", "SASO PCoC"], notes: "" },
    "tiles-sanitaryware":{ duty: "5% + 15% VAT", certs: ["SABER", "SASO PCoC"], notes: "" },
    luggage:             { duty: "5% + 15% VAT", certs: [], notes: "" },
    "mobile-accessories":{ duty: "5-15% + 15% VAT", certs: ["SABER", "CITC type approval"], notes: "" },
    machinery:           { duty: "5% + 15% VAT", certs: ["SABER", "SASO Machinery"], notes: "" },
    "mining-machinery":  { duty: "5% + 15% VAT", certs: ["SABER", "SASO"], notes: "" },
    "bopet-films":       { duty: "5% + 15% VAT", certs: [], notes: "" },
    textiles:            { duty: "5% + 15% VAT", certs: ["SABER", "SFDA where applicable"], notes: "" },
    "technical-fabrics": { duty: "5% + 15% VAT", certs: [], notes: "" },
    stationery:          { duty: "5% + 15% VAT", certs: [], notes: "" },
    "yiwu-commodities":  { duty: "5% + 15% VAT (most lines)", certs: ["SABER for regulated SKUs"], notes: "" },
    lighting:            { duty: "5% + 15% VAT", certs: ["SABER", "SASO Energy Efficiency"], notes: "" },
  },
};

export function getCompliance(productSlug, countryCode) {
  const c = COUNTRIES.find(x => x.code === countryCode);
  const p = PRODUCTS.find(x => x.slug === productSlug);
  const m = (DUTY_MATRIX[countryCode] || {})[productSlug];
  if (!c || !p || !m) return null;
  return { country: c, product: p, ...m };
}

export const COMPLIANCE_PAIRS = (() => {
  const out = [];
  for (const c of COUNTRIES) {
    for (const p of PRODUCTS) {
      if ((DUTY_MATRIX[c.code] || {})[p.slug]) out.push({ country: c, product: p });
    }
  }
  return out;
})();
